import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Hammer, AlertTriangle, Undo2, Trash2, ScanLine, Upload, PenLine, Archive, ChevronDown, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FIELDS = {
  raas_id: "", description: "", part_number: "", serial_no: "", trace: "", exp_date: "", location: "",
};

const todayISO = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Edmonton" });

function isExpired(expDate) {
  if (!expDate) return false;
  const d = new Date(`${String(expDate).slice(0, 10)}T23:59:59`);
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date();
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", {
      timeZone: "America/Edmonton", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Downscale a captured/uploaded photo to a reasonable JPEG for the OCR call. */
function downscaleToJpeg(dataUrl, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => reject(new Error("Could not read image"));
    img.src = dataUrl;
  });
}

const FIELD_DEFS = [
  { key: "description", label: "Description", required: true, testId: "tool-field-description" },
  { key: "part_number", label: "Part #", testId: "tool-field-part" },
  { key: "serial_no", label: "Serial No", testId: "tool-field-serial" },
  { key: "trace", label: "Trace", testId: "tool-field-trace" },
  { key: "exp_date", label: "Exp Date (YYYY-MM-DD)", testId: "tool-field-exp" },
  { key: "raas_id", label: "RAAS ID", testId: "tool-field-raas" },
  { key: "location", label: "Shelf Location", testId: "tool-field-location" },
];

export default function ToolStatus() {
  const [tools, setTools] = useState([]);
  const [archive, setArchive] = useState([]);
  const [me, setMe] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveQuery, setArchiveQuery] = useState("");
  const filteredArchive = React.useMemo(() => {
    const q = archiveQuery.trim().toLowerCase();
    if (!q) return archive;
    return archive.filter((t) =>
      [t.description, t.part_number, t.serial_no, t.trace, t.raas_id, t.engineer_name]
        .some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [archive, archiveQuery]);

  // Scan dialog state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("camera"); // "camera" | "review"
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [photo, setPhoto] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [engineer, setEngineer] = useState("");
  const [notes, setNotes] = useState("");
  const [signedOutDate, setSignedOutDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const refresh = React.useCallback(() => {
    api.get("/tool-status").then((r) => setTools(r.data || []));
    api.get("/tool-status/archive").then((r) => setArchive(r.data || []));
  }, []);

  useEffect(() => {
    refresh();
    api.get("/auth/me").then((r) => setMe(r.data));
  }, [refresh]);

  const stopCamera = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Live camera preview while the scan dialog shows the camera step.
  useEffect(() => {
    if (!open || step !== "camera") {
      stopCamera();
      return undefined;
    }
    let cancelled = false;
    setCameraError("");
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => {
        if (!cancelled) setCameraError("Camera unavailable — upload a photo of the tag instead.");
      });
    return () => { cancelled = true; stopCamera(); };
  }, [open, step, stopCamera]);

  const openScan = () => {
    setFields(EMPTY_FIELDS); setEngineer(""); setNotes(""); setPhoto(null); setSignedOutDate(todayISO());
    setStep("camera"); setOpen(true);
  };
  const openManual = () => {
    setFields(EMPTY_FIELDS); setEngineer(""); setNotes(""); setPhoto(null); setSignedOutDate(todayISO());
    setStep("review"); setOpen(true);
  };

  const runScan = async (dataUrl) => {
    setScanning(true);
    try {
      const jpeg = await downscaleToJpeg(dataUrl);
      setPhoto(jpeg);
      const { data } = await api.post("/tool-status/scan", { image_base64: jpeg });
      const f = data.fields || {};
      setFields({
        raas_id: f.raas_id || "", description: f.description || "", part_number: f.part_number || "",
        serial_no: f.serial_no || "", trace: f.trace || "", exp_date: f.exp_date || "", location: f.location || "",
      });
      setStep("review");
      if (data.expired) toast.warning("This tool's Exp Date has passed — check before signing out.");
      else toast.success("Tag read — review the fields");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the tag — try again or enter manually.");
      setStep("camera");
    } finally {
      setScanning(false);
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) { toast.error("Camera not ready yet"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    stopCamera();
    runScan(canvas.toDataURL("image/jpeg", 0.92));
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { stopCamera(); runScan(reader.result); };
    reader.readAsDataURL(file);
  };

  const signOut = async () => {
    if (!fields.description.trim()) { toast.error("Description is required"); return; }
    if (!engineer.trim()) { toast.error("Enter the engineer taking the tool"); return; }
    setSaving(true);
    try {
      await api.post("/tool-status", {
        description: fields.description, part_number: fields.part_number || null,
        serial_no: fields.serial_no || null, trace: fields.trace || null,
        exp_date: fields.exp_date || null, raas_id: fields.raas_id || null,
        location: fields.location || null, engineer_name: engineer, notes: notes || null,
        signed_out_date: signedOutDate || null,
      });
      toast.success(`Signed out to ${engineer.trim()}`);
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign out tool");
    } finally {
      setSaving(false);
    }
  };

  const returnTool = async (t) => {
    try {
      await api.post(`/tool-status/${t.id}/return`);
      toast.success(`${t.description} returned — archived for 10 days`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to return tool");
    }
  };

  const deleteTool = async (t) => {
    if (!window.confirm(`Delete the sign-out record for "${t.description}"? This does not archive it.`)) return;
    try {
      await api.delete(`/tool-status/${t.id}`);
      toast.success("Record deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const reviewExpired = isExpired(fields.exp_date);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="tool-status-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · Tool Tracking</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Tool Status</h1>
          <p className="mt-2 text-sm text-gray-500 max-w-xl">
            Scan the tool tag with your camera to log who has it. Returned tools stay in the archive for 10 days, then auto-delete.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openManual} data-testid="tool-manual-btn">
            <PenLine className="w-4 h-4 mr-1.5" /> Manual entry
          </Button>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft" onClick={openScan} data-testid="tool-scan-btn">
            <Camera className="w-4 h-4 mr-1.5" /> Scan tag
          </Button>
        </div>
      </div>

      {/* ---------------- Signed-out tools ---------------- */}
      <div className="mt-8 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-summit-hairline bg-summit-glacier/50 flex items-center gap-2">
          <Hammer className="w-4 h-4 text-summit-primary" />
          <span className="font-display text-sm font-medium tracking-tight">Signed out</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{tools.length} tool{tools.length === 1 ? "" : "s"}</span>
        </div>
        {tools.length === 0 ? (
          <div className="p-10 text-center font-mono text-[11px] uppercase tracking-widest text-gray-400" data-testid="tool-empty-state">
            No tools signed out
          </div>
        ) : (
          <>
          {/* -------- Mobile cards (PWA) -------- */}
          <div className="sm:hidden divide-y divide-summit-hairline" data-testid="tool-mobile-list">
            {tools.map((t) => (
              <div key={t.id} className="p-4" data-testid={`tool-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-summit-ink leading-snug">{t.description}</div>
                    {t.raas_id && <div className="font-mono text-[10px] text-gray-400 mt-0.5">{t.raas_id}</div>}
                  </div>
                  {t.expired && (
                    <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-mono text-[9px] uppercase tracking-widest" data-testid={`tool-expired-m-${t.id}`}>
                      <AlertTriangle className="w-3 h-3" /> Expired
                    </span>
                  )}
                </div>
                <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
                  {[
                    ["Part #", t.part_number],
                    ["Serial No", t.serial_no],
                    ["Trace", t.trace],
                    ["Exp Date", t.exp_date, t.expired],
                  ].map(([label, val, bad]) => (
                    <div key={label}>
                      <dt className="font-mono text-[9px] uppercase tracking-widest text-gray-400">{label}</dt>
                      <dd className={`font-mono text-xs mt-0.5 ${bad ? "text-rose-600 font-semibold" : val ? "text-summit-ink" : "text-gray-300"}`}>{val || "—"}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
                  <span className="inline-flex px-2 py-0.5 rounded-full bg-summit-glacier font-mono text-[11px]" data-testid={`tool-engineer-m-${t.id}`}>{t.engineer_name}</span>
                  <span className="font-mono text-[10px] text-gray-400">Out {t.signed_out_date || (t.signed_out_at || "").slice(0, 10)} · {t.signed_out_by_name}</span>
                </div>
                {t.notes && <div className="mt-1.5 text-xs text-gray-500">{t.notes}</div>}
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-9 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => returnTool(t)}
                    data-testid={`tool-return-m-${t.id}`}
                  >
                    <Undo2 className="w-4 h-4 mr-1.5" /> Returned
                  </Button>
                  {(me?.role === "admin" || me?.id === t.signed_out_by_id) && (
                    <button
                      type="button"
                      onClick={() => deleteTool(t)}
                      className="p-2.5 rounded-md border border-summit-hairline text-gray-400 hover:text-rose-600 hover:bg-rose-50"
                      aria-label="Delete record"
                      data-testid={`tool-delete-m-${t.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* -------- Desktop table -------- */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-summit-canvas">
                  {["Description", "Part #", "Serial No", "Trace", "Exp Date", "Signed out to", "Date out", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.id} className="border-t border-summit-hairline" data-testid={`tool-row-${t.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-summit-ink">{t.description}</div>
                      {t.raas_id && <div className="font-mono text-[10px] text-gray-400">{t.raas_id}</div>}
                      {t.notes && <div className="text-xs text-gray-500 mt-0.5">{t.notes}</div>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{t.part_number || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{t.serial_no || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{t.trace || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {t.exp_date ? (
                        <span className={`font-mono text-xs ${t.expired ? "text-rose-600 font-semibold" : ""}`}>{t.exp_date}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                      {t.expired && (
                        <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-mono text-[9px] uppercase tracking-widest" data-testid={`tool-expired-${t.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Expired
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-summit-glacier font-mono text-[11px]" data-testid={`tool-engineer-${t.id}`}>{t.engineer_name}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-gray-600 whitespace-nowrap" data-testid={`tool-dateout-${t.id}`}>
                      {t.signed_out_date || (t.signed_out_at || "").slice(0, 10)}
                      <div className="text-[9px] text-gray-400">{fmtDateTime(t.signed_out_at)} · by {t.signed_out_by_name}</div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50 text-[11px]"
                        onClick={() => returnTool(t)}
                        data-testid={`tool-return-${t.id}`}
                      >
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Returned
                      </Button>
                      {(me?.role === "admin" || me?.id === t.signed_out_by_id) && (
                        <button
                          type="button"
                          onClick={() => deleteTool(t)}
                          className="ml-1.5 p-1.5 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50"
                          aria-label="Delete record"
                          data-testid={`tool-delete-${t.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* ---------------- Returned archive ---------------- */}
      <div className="mt-6 border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid="tool-archive-section">
        <button
          type="button"
          onClick={() => setArchiveOpen((o) => !o)}
          className="w-full px-4 py-3 flex items-center gap-2 hover:bg-summit-glacier/40"
          data-testid="tool-archive-toggle"
        >
          <Archive className="w-4 h-4 text-gray-500" />
          <span className="font-display text-sm font-medium tracking-tight">Returned</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{archive.length} · auto-deletes after 10 days</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${archiveOpen ? "rotate-180" : ""}`} />
        </button>
        {archiveOpen && (
          <>
          <div className="px-4 py-2.5 border-t border-summit-hairline bg-summit-canvas/40">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-4 h-4 text-gray-400" />
              <Input
                value={archiveQuery}
                onChange={(e) => setArchiveQuery(e.target.value)}
                placeholder="Search returned tools — description, part #, serial, engineer…"
                className="pl-8 h-8 text-sm"
                data-testid="tool-archive-search"
              />
            </div>
          </div>
          {archive.length === 0 ? (
            <div className="p-8 text-center font-mono text-[11px] uppercase tracking-widest text-gray-400 border-t border-summit-hairline">
              Nothing returned in the last 10 days
            </div>
          ) : filteredArchive.length === 0 ? (
            <div className="p-8 text-center font-mono text-[11px] uppercase tracking-widest text-gray-400 border-t border-summit-hairline" data-testid="tool-archive-no-match">
              No returned tools match the search
            </div>
          ) : (
            <>
            {/* -------- Mobile cards (PWA) -------- */}
            <div className="sm:hidden divide-y divide-summit-hairline border-t border-summit-hairline">
              {filteredArchive.map((t) => (
                <div key={t.id} className="p-4 opacity-80" data-testid={`tool-archive-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-summit-ink leading-snug">{t.description}</div>
                      <div className="font-mono text-[10px] text-gray-400 mt-0.5">
                        {t.part_number || "—"}{t.serial_no ? ` · S/N ${t.serial_no}` : ""}
                      </div>
                    </div>
                    <span className="shrink-0 inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-mono text-[10px] uppercase tracking-widest">
                      {t.days_left != null ? `${t.days_left}d` : "—"}
                    </span>
                  </div>
                  <div className="mt-2 font-mono text-[10px] text-gray-400">
                    Was with <span className="text-gray-600">{t.engineer_name}</span> · returned {fmtDateTime(t.returned_at)} by {t.returned_by_name}
                  </div>
                </div>
              ))}
            </div>
            {/* -------- Desktop table -------- */}
            <div className="hidden sm:block overflow-x-auto border-t border-summit-hairline">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-summit-canvas">
                    {["Description", "Part #", "Serial No", "Was with", "Returned", "Deletes in"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredArchive.map((t) => (
                    <tr key={t.id} className="border-t border-summit-hairline opacity-80" data-testid={`tool-archive-row-${t.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="text-summit-ink">{t.description}</div>
                        {t.raas_id && <div className="font-mono text-[10px] text-gray-400">{t.raas_id}</div>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{t.part_number || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{t.serial_no || "—"}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px]">{t.engineer_name}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-gray-500 whitespace-nowrap">
                        {fmtDateTime(t.returned_at)}
                        <div className="text-[9px] text-gray-400">by {t.returned_by_name}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-mono text-[10px] uppercase tracking-widest">
                          {t.days_left != null ? `${t.days_left}d` : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          </>
        )}
      </div>

      {/* ---------------- Scan / sign-out dialog ---------------- */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) { stopCamera(); setOpen(false); } }}>
        <DialogContent className="max-w-lg" data-testid="tool-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-4 h-4" /> {step === "camera" ? "Scan tool tag" : "Sign out tool"}
            </DialogTitle>
            <DialogDescription>
              {step === "camera"
                ? "Hold the tag flat and fill the frame — the fields are read automatically."
                : "Check the fields read from the tag, then enter who is taking the tool."}
            </DialogDescription>
          </DialogHeader>

          {step === "camera" && (
            <div className="space-y-3">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="tool-camera-video" />
                {scanning && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 text-white">
                    <ScanLine className="w-8 h-8 animate-pulse" />
                    <span className="font-mono text-[11px] uppercase tracking-widest">Reading tag…</span>
                  </div>
                )}
                {cameraError && !scanning && (
                  <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-white/80 text-sm" data-testid="tool-camera-error">
                    {cameraError}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="tool-upload-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> Upload photo
                </Button>
                <Button className="bg-summit-primary hover:bg-summit-primary-soft" onClick={capture} disabled={scanning || !!cameraError} data-testid="tool-capture-btn">
                  <Camera className="w-4 h-4 mr-1.5" /> Capture
                </Button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="tool-upload-input" />
              <button
                type="button"
                className="w-full text-center font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-gray-600"
                onClick={() => { stopCamera(); setStep("review"); }}
                data-testid="tool-skip-to-manual"
              >
                Skip — type it in manually
              </button>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-3">
              {photo && (
                <div className="flex items-center gap-3">
                  <img src={photo} alt="Scanned tag" className="w-20 h-14 object-cover rounded border border-summit-hairline" data-testid="tool-photo-preview" />
                  <button
                    type="button"
                    className="font-mono text-[10px] uppercase tracking-widest text-sky-600 hover:text-sky-800"
                    onClick={() => setStep("camera")}
                    data-testid="tool-rescan-btn"
                  >
                    Re-scan
                  </button>
                </div>
              )}
              {reviewExpired && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-xs" data-testid="tool-expired-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>This tool&apos;s Exp Date ({fields.exp_date}) is older than today — calibration may have EXPIRED. Verify before signing it out.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                {FIELD_DEFS.map((f) => (
                  <div key={f.key} className={f.key === "description" ? "col-span-2" : ""}>
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      {f.label}{f.required && <span className="text-rose-500"> *</span>}
                    </Label>
                    <Input
                      value={fields[f.key]}
                      onChange={(e) => setFields((m) => ({ ...m, [f.key]: e.target.value }))}
                      className={`mt-1 h-8 text-sm ${f.key === "exp_date" && reviewExpired ? "border-rose-400 text-rose-700" : ""}`}
                      data-testid={f.testId}
                    />
                  </div>
                ))}
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Sign-out date</Label>
                  <Input
                    type="date"
                    value={signedOutDate}
                    onChange={(e) => setSignedOutDate(e.target.value)}
                    className="mt-1 h-8 text-sm"
                    data-testid="tool-field-signout-date"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-summit-primary">
                    Signed out to — engineer<span className="text-rose-500"> *</span>
                  </Label>
                  <Input
                    value={engineer}
                    onChange={(e) => setEngineer(e.target.value)}
                    placeholder="e.g. M. Bartle"
                    className="mt-1 h-9"
                    data-testid="tool-field-engineer"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Notes · optional</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. taken to YZF hangar for engine change"
                    className="mt-1"
                    data-testid="tool-field-notes"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { stopCamera(); setOpen(false); }} data-testid="tool-dialog-cancel">Cancel</Button>
            {step === "review" && (
              <Button
                className="bg-summit-primary hover:bg-summit-primary-soft"
                onClick={signOut}
                disabled={saving}
                data-testid="tool-signout-submit"
              >
                {saving ? "Saving…" : "Sign out tool"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
